import React from 'react'
import type { JSX } from 'react'

import type { ButtonIntent } from './__internal__/button-intent'
import { ButtonLoader } from './__internal__/button-loader'

/**
 * Button Component
 *
 * A filled button component with rounded corners and intent-based styling.
 *
 * Usage Examples:
 * <Button intent={"primary"} onClick={() => console.log('clicked')}>Click me</Button>
 * <Button intent={"primary-dark"}>Click me</Button>
 * <Button intent={"secondary"}>Submit</Button>
 * <Button intent={"secondary-dark"}>Submit</Button>
 * <Button intent={"primary"}><Button.Loader variant="light" /></Button>
 */

export function Button({
  children,
  intent,
  ...buttonHTMLAttributes
}: {
  readonly children: React.ReactNode
  readonly intent: ButtonIntent
} & Omit<
  JSX.IntrinsicElements['button'],
  // Omitted because not allowed:
  'className' | 'disabled' // Avoid 'disabled' since it's not accessibility friendly. Use 'aria-disabled' and 'readOnly' instead.
>) {
  return (
    <button
      className={`my-[10px] w-full rounded-full transition-colors duration-100 px-3.5 py-[11px] grove-typography-button cursor-pointer flex items-center justify-center gap-2 ${
        intent === 'primary'
          ? 'bg-acorns-green hover:bg-[#71c345] contrast-more:bg-acorns-ada-dark-green text-white aria-disabled:bg-acorns-stone-light'
          : intent === 'primary-dark'
            ? 'bg-white hover:bg-acorns-ivory aria-disabled:bg-acorns-stone-light aria-disabled:text-white'
            : intent === 'secondary'
              ? 'border border-acorns-stone-light text-acorns-slate hover:border-acorns-stone aria-disabled:text-acorns-stone-light aria-disabled:border-acorns-stone-light'
              : 'border border-white text-white hover:bg-[rgba(255,255,255,0.05)] aria-disabled:text-acorns-stone-light aria-disabled:border-acorns-stone-light aria-disabled:hover:bg-transparent'
      } aria-disabled:cursor-not-allowed`}
      type={'button'}
      {...buttonHTMLAttributes}
    >
      {children}
    </button>
  )
}

Button.Loader = ButtonLoader
